import {EndPoints, HttpMethods} from 'interfaces';
import {
  Connection,
  CONNECTION_TYPE,
  Connections,
  Message,
  MESSAGE_RECEIVER_TYPE,
  MESSAGE_TYPE,
  MessageResponse,
  MessageStatusUpdate,
  NormalizedConnections,
  NormalizedMessageResponse,
} from 'interfaces/Pages/Messaging';
import {normalize, schema} from 'normalizr';
import {Response} from 'redaxios';
import {unwrapAPIError} from 'utils';
import Logger from 'utils/Logger';

import {CometChat} from '@cometchat-pro/chat';

import {requestHandler} from '../HTTP';

import {decryptMessage} from './utils';

const CHAT_DECRYPTION_KEY = process.env.REACT_APP_CHAT_DECRYPTION_KEY as string;

const connections = new schema.Entity<Connection>(
  'connections',
  {},
  {
    idAttribute: (value): string => {
      return value.uid;
    },
  },
);
const messages = new schema.Entity<Message>(
  'messages',
  {},
  {
    idAttribute: (value): string => {
      return value.id;
    },
  },
);

const arrayOfUsers = new schema.Array(connections);
const arrayOfMessages = new schema.Array(messages);

const getConnections = async (data: {
  type: CONNECTION_TYPE;
  limit: number;
  page: number;
  keyword?: string;
}): Promise<NormalizedConnections> => {
  let url = `${EndPoints.GetConnections}?type=${data.type}&limit=${data.limit}&page=${data.page}`;
  if (data.keyword) {
    url += `&searchKey=${data.keyword}`;
  }
  try {
    const res = await requestHandler<{
      message: Connections;
    }>({
      method: HttpMethods.GET,
      url: url as unknown as EndPoints,
    });

    const {
      message: {data: users, ...rest},
    } = res.data;

    const normalizedUsers = normalize(users, arrayOfUsers);

    return {
      ...rest,
      ...normalizedUsers,
    } as unknown as NormalizedConnections;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};
const getAConnection = async (uid: string): Promise<Connection> => {
  const url = `${EndPoints.GetConnections}?type=${
    CONNECTION_TYPE.ALL
  }&limit=${1}&page=${1}&searchKey=${uid}`;

  try {
    const res = await requestHandler<{
      message: Connections;
    }>({
      method: HttpMethods.GET,
      url: url as unknown as EndPoints,
    });

    const {
      message: {data: users},
    } = res.data;

    return users?.[0] ?? {};
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const getMessages = async (data: {
  conversationId: string;
  limit: number;
  lastMessageId: string | null;
}): Promise<NormalizedMessageResponse> => {
  let url = `${EndPoints.GetMessages}?conversationId=${data.conversationId}&limit=${data.limit}`;
  if (data.lastMessageId) {
    url += `&lastMessageId=${data.lastMessageId}`;
  }
  try {
    const res = await requestHandler<{
      message: {
        data: {iv: string; content: string; tag: string}[];
        hasMore: boolean;
        lastMessageId: string;
      };
    }>({
      method: HttpMethods.GET,
      url: url as unknown as EndPoints,
    });

    const {
      message: {data: messages, ...rest},
    } = res.data;

    const decryptedMessages = (await Promise.all(
      messages.map(message => {
        const decryptedMessage = decryptMessage({
          ...message,
          keyHex: CHAT_DECRYPTION_KEY,
        });
        return decryptedMessage;
      }),
    )) as unknown as MessageResponse['data'];

    const normalizedMessages = normalize(decryptedMessages, arrayOfMessages);

    return {
      ...rest,
      entities: normalizedMessages.entities,
      result: normalizedMessages.result,
    } as unknown as NormalizedMessageResponse;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const getMediaMessages = async (data: {
  uid: string;
  limit: number;
  types: MESSAGE_TYPE[];
  lastMessageId: string | null;
}) => {
  try {
    const res = await requestHandler<{
      message: {
        data: {iv: string; content: string; tag: string}[];
        hasMore: boolean;
        lastMessageId: string;
      };
    }>({
      method: HttpMethods.GET,
      url: EndPoints.GetMediaMessages,
      options: {
        params: {
          uid: data.uid,
          types: data.types.join(','),
          limit: data.limit,
          lastMessageId: data.lastMessageId ?? null,
        },
      },
    });

    const {
      message: {data: messages, ...rest},
    } = res.data;

    const decryptedMessages = (await Promise.all(
      messages.map(message => {
        const decryptedMessage = decryptMessage({
          ...message,
          keyHex: CHAT_DECRYPTION_KEY,
        });
        return decryptedMessage;
      }),
    )) as unknown as MessageResponse['data'];

    return {
      data: decryptedMessages,
      ...rest,
    };
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const getUnreadAllMessageCount = async () => {
  try {
    const res = await CometChat.getUnreadMessageCountForAllUsers();

    return Object.values(res)?.[0] ?? 0;
  } catch (error) {
    Logger.error('getAllUnreadMessagesCount error', error);
    throw error;
  }
};

const getMessageBlob = async (fileId: string): Promise<Response<Blob>> => {
  try {
    const res = await requestHandler<Blob>({
      method: HttpMethods.GET,
      url: `${EndPoints.GetMessageBlob}/${fileId}` as unknown as EndPoints,
      options: {
        responseType: 'blob',
      },
    });

    return res;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

const sendMessage = async (data: {
  receiver: string;
  receiverType: MESSAGE_RECEIVER_TYPE;
  messageType: MESSAGE_TYPE;
  text?: string;
  file?: File;
}): Promise<Response<{message: Message}>> => {
  try {
    const formData = new FormData();

    formData.append('receiver', data.receiver);
    formData.append('receiverType', data.receiverType);
    formData.append('messageType', data.messageType);

    if (data.messageType === MESSAGE_TYPE.TEXT && data.text) {
      formData.append('text', data.text);
    } else if (data.file) {
      formData.append('file', data.file);
    }

    const res = await requestHandler<{message: Message}>({
      method: HttpMethods.POST,
      url: EndPoints.SendMessage,
      data: formData,
    });

    return res;
  } catch (error) {
    const errorValue = unwrapAPIError(error);
    return Promise.reject(errorValue);
  }
};

/**
 * Mark message as delivered
 *
 * @param messages - {@link MessageStatusUpdate}
 */
const markAsRead = (messages: MessageStatusUpdate[]): void => {
  const readPromises = messages.map(message =>
    CometChat.markAsRead(
      message.id,
      message.receiver.uid,
      message.receiverType,
      message.sender.uid,
    ).then(
      () => {
        Logger.info(`Message ${message.id} marked as read.`);
      },
      (error: CometChat.CometChatException) => {
        Logger.error(
          `An error occurred when marking message ${message.id} as read.`,
          error,
        );
      },
    ),
  );

  // Wait for all promises to complete
  Promise.all(readPromises).then(() => {
    Logger.info('All messages marked as read.');
  });
};

/**
 * Mark message as delivered
 *
 * @param messages - {@link IncomingMessage}
 */
const markAsDelivered = (messages: MessageStatusUpdate[]): void => {
  const deliveryPromises = messages.map(message =>
    CometChat.markAsDelivered(
      message.id,
      message.receiver.uid,
      message.receiverType,
      message.sender.uid,
    ).then(
      () => {
        Logger.info(`Message ${message.id} marked as delivered.`);
      },
      (error: CometChat.CometChatException) => {
        Logger.error(
          `An error occurred when marking message ${message.id} as delivered.`,
          error,
        );
      },
    ),
  );

  // Wait for all promises to complete
  Promise.all(deliveryPromises).then(() => {
    Logger.info('All messages marked as delivered.');
  });
};

export const MessagingService = {
  getConnections,
  getAConnection,
  getMessages,
  getMessageBlob,
  getUnreadAllMessageCount,
  sendMessage,
  markAsRead,
  markAsDelivered,
  getMediaMessages,
};
